import { memo, useEffect, useState, useRef } from "react"

import ProductService from "@/services/product"
import { Stack, Text, Image, AspectRatio, Center, Button, Container, Box, Table, IconButton, Flex, VStack, Separator, Drawer, Portal, CloseButton, Input } from "@chakra-ui/react"
import { useCart } from "contexts/cart"
import { formatCurrency } from "@/utilities/number"
import { LuPencil } from "react-icons/lu"
import {
    DrawerBackdrop,
    DrawerBody,
    DrawerCloseTrigger,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerRoot,
    DrawerTitle,
    DrawerTrigger,
    DrawerActionTrigger
} from "@/components/ui/drawer"
import { StepperInput } from "@/components/ui/stepper-input"
import { useApp } from "contexts/app"
import { useStore } from "contexts/store"
import { useNavigate } from "react-router"
import VoucherInput from "./VoucherInput"
import PointInput from "./PointInput"
const Cart = () => {
    const { setProcessing, setPageTitle, showToast, setShowProductSearch } = useApp();

    const { cart, updateLineItem, removeLineItem, calculateItemPrice, validateInventory } = useCart();
    const [lineItems, setLineItems] = useState([]);
    const [editLineItem, setEditLineItem] = useState(false);
    const [lineToEdit, setLineToEdit] = useState()
    const [itemCaculatedPrice, setItemCaculatedPrice] = useState(0);
    const [inventoryErrorsByVariant, setInventoryErrorsByVariant] = useState({});
    const { store } = useStore();
    const navigate = useNavigate();

    const getImageSrc = (image) => {
        if (!image) return undefined;
        if (typeof image === "string") return image;
        return image.sm_url || image.md_url || image.url;
    }

    const getLineItemImageSrc = (lineItem) => {
        const variantImage = lineItem?.variant?.image || lineItem?.variant?.images?.[0];
        const productImage = lineItem?.product?.thumbnail || lineItem?.product?.images?.[0];
        return getImageSrc(variantImage) || getImageSrc(productImage);
    }

    const getPriceRowsForLineEdit = (line = null) => {
        const priceList = Array.isArray(line?.variant?.price_list) ? line.variant.price_list : [];
        if (priceList.length > 0) {
            return [...priceList].sort((a, b) => Number(a?.min || 0) - Number(b?.min || 0));
        }
        return [];
    }

    useEffect(() => {
        setPageTitle("Giỏ hàng")
    }, [setPageTitle])

    useEffect(() => {
        setLineItems(cart?.line_items ?? [])
    }, [cart])

    useEffect(() => {

        const loadPrice = async () => {
            var price = await calculateItemPrice(lineToEdit.variant, lineToEdit.quantity)
            setItemCaculatedPrice(price)
        }
        if (lineToEdit) {
            loadPrice();
        }

    }, [lineToEdit])

    const onEditLineItem = async (line) => {
        setLineToEdit({ ...line });
        setEditLineItem(true);

    }

    const mapInventoryErrors = (errors = []) => {
        const map = {};
        for (let index = 0; index < errors.length; index++) {
            const item = errors[index] ?? {};
            const keys = [item.variant_id, item.product_id, item.id];
            const normalized = {
                ...item,
                available: Number(item.available ?? 0)
            };

            for (let keyIndex = 0; keyIndex < keys.length; keyIndex++) {
                const key = keys[keyIndex];
                if (!key) {
                    continue;
                }
                map[String(key)] = normalized;
            }
        }
        return map;
    }

    const canCheckOut = () => {
        var orderSetting = store.order_setting;

        if (orderSetting.min_quantity > 0) {
            if (cart.total_item < orderSetting.min_quantity) {
                showToast({
                    description: `Bạn cần đặt tối thiểu ${orderSetting.min_quantity} sản phẩm`, type: "error",
                    duration: 6000,

                });
                return false;
            }
        }

        if (orderSetting.min_amount > 0) {
            if (cart.sub_amount < orderSetting.min_amount) {
                showToast({
                    description: `Bạn cần đặt tối thiểu   ${formatCurrency(orderSetting.min_amount)} VND`, type: "error",
                    duration: 6000,

                });
                return false;
            }
        }
        return true



    }


    const goToCheckOut = async () => {
        if (!canCheckOut()) {
            return;
        }

        setProcessing(true);
        try {
            await validateInventory();
            setInventoryErrorsByVariant({});
            navigate("/checkout")
        } catch (error) {
            const inventoryErrors = Array.isArray(error?.inventory_errors) ? error.inventory_errors : [];
            const mapped = mapInventoryErrors(inventoryErrors);
            setInventoryErrorsByVariant(mapped);
            showToast({
                description: "Một số sản phẩm không đủ tồn kho. Vui lòng cập nhật số lượng.",
                type: "error",
                duration: 6000,
            })
        } finally {
            setProcessing(false);
        }
    }

    const onApplyAvailableQuantity = async (item) => {
        const inventoryError = inventoryErrorsByVariant[item.product_id] ?? inventoryErrorsByVariant[item.variant_id];
        if (!inventoryError) {
            return;
        }

        const available = Number(inventoryError.available ?? 0);
        setProcessing(true);
        try {
            if (available <= 0) {
                await removeLineItem(item.product_id);
                setInventoryErrorsByVariant(prev => {
                    const next = { ...prev };
                    delete next[item.product_id];
                    delete next[item.variant_id];
                    return next;
                });
                showToast({ description: "Món hàng đã hết hàng đã được xoá khỏi giỏ hàng.", type: "success" })
                return;
            }

            const nextQuantity = Math.max(1, available);
            await updateLineItem(item.product_id, nextQuantity);
            setInventoryErrorsByVariant(prev => {
                const next = { ...prev };
                delete next[item.product_id];
                delete next[item.variant_id];
                return next;
            });
            showToast({ description: `Đã cập nhật số lượng còn lại là ${nextQuantity} sản phẩm.`, type: "success" })
        } finally {
            setProcessing(false);
        }
    }



    const onUpdateLineItem = async () => {
        setProcessing(true);
        try {
            await updateLineItem(lineToEdit.product_id, lineToEdit.quantity);
            setLineToEdit(null);
            showToast({ description: "Đã cập nhật giỏ hàng", type: "success" })
        } catch (error) {
            const inventoryErrors = Array.isArray(error?.inventory_errors) ? error.inventory_errors : [];
            const first = inventoryErrors[0] ?? {};
            if (first.reason == "insufficient_available") {
                const available = Number(first.available ?? 0);
                showToast({
                    description: available <= 0
                        ? "Số lượng vượt quá tồn kho. Hết hàng."
                        : `Số lượng vượt quá tồn kho. Chỉ còn ${available} sản phẩm có thể đặt.`,
                    type: "error"
                })
            } else {
                showToast({
                    description: "Không thể cập nhật số lượng do tồn kho không đủ.",
                    type: "error"
                })
            }
        } finally {
            setProcessing(false);
        }
    }

    const onRemoveLineItem = async () => {

        setProcessing(true);
        await removeLineItem(lineToEdit.product_id);
        setLineToEdit(null);
        setProcessing(false);
    }

    if (!cart) {
        return (
            <EmptyCart></EmptyCart>
        )
    }


    if (cart?.line_items?.length == 0) {
        return (
            <EmptyCart></EmptyCart>
        )
    }





    return (
        <div style={{ paddingBottom: 75 }}>
            <VStack>



                {lineItems.map((item) => (
                    <div key={item.product_id} style={{ width: "100%" }} onClick={e => { onEditLineItem(item) }}>
                        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexDirection: "row" }}>
                            <div style={{ flex: 0.3 }}>
                                <AspectRatio ratio={4 / 3}>
                                    <Center>
                                        <Image src={getLineItemImageSrc(item)}></Image>
                                    </Center>
                                </AspectRatio>

                            </div>
                            <div style={{ flex: 0.7, overflow: "hidden" }}>

                                <Text truncate lineClamp={2}  >{item.product.name}</Text>


                                <div >
                                    <Text fontWeight="light" >  {item?.variant?.variant_title || ""} </Text>


                                    {inventoryErrorsByVariant[item.product_id] &&
                                        <div style={{ marginTop: 6 }}>
                                            {inventoryErrorsByVariant[item.product_id].reason == "variant_not_found" &&
                                                <Text color="red.500" fontSize="sm">Sản phẩm không còn tồn tại. Vui lòng cập nhật lại số lượng.</Text>
                                            }
                                            {inventoryErrorsByVariant[item.product_id].reason != "variant_not_found" &&
                                                <Text color="red.500" fontSize="sm">
                                                    {Number(inventoryErrorsByVariant[item.product_id].available ?? 0) <= 0
                                                        ? "Hết hàng."
                                                        : `Chỉ còn ${inventoryErrorsByVariant[item.product_id].available} sản phẩm có thể đặt.`}
                                                </Text>
                                            }
                                            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        await onApplyAvailableQuantity(item)
                                                    }}
                                                >
                                                    {Number(inventoryErrorsByVariant[item.product_id]?.available ?? inventoryErrorsByVariant[item.variant_id]?.available ?? 0) <= 0
                                                        ? "Bỏ món hết hàng"
                                                        : "Đặt số còn lại"}
                                                </Button>
                                                {Number(inventoryErrorsByVariant[item.product_id]?.available ?? inventoryErrorsByVariant[item.variant_id]?.available ?? 0) > 0 && (
                                                    <Button
                                                        size="xs"
                                                        variant="outline"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            await removeLineItem(item.product_id);
                                                        }}
                                                    >
                                                        Bỏ món
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    }


                                </div>
                                <div style={{ display: "flex" }}>
                                    <div style={{ flex: 0.15, textAlign: "right" }}>
                                        <Text>{formatCurrency(item.quantity)}</Text>
                                    </div>
                                    <div style={{ flex: 0.4, textAlign: "right" }}>
                                        <Text>{formatCurrency(item.price)}</Text>
                                    </div>
                                    <div style={{ flex: 0.4, textAlign: "right" }}>
                                        <Text>{formatCurrency(item.amount)}</Text>
                                    </div>

                                </div>

                            </div>
                        </div>

                        <Separator />

                    </div>

                ))
                }


                <Table.Root size="sm">

                    <Table.Body style={{ fontWeight: "500" }}>

                        <Table.Row >
                            <Table.Cell>Thành tiền</Table.Cell>
                            <Table.Cell textAlign="end">{formatCurrency(cart.sub_amount)}</Table.Cell>
                        </Table.Row>


                        <Table.Row >
                            <Table.Cell colSpan={2}>
                                <VoucherInput></VoucherInput>
                            </Table.Cell>
                        </Table.Row>

                        <Table.Row >
                            <Table.Cell>   <PointInput></PointInput></Table.Cell>
                            <Table.Cell textAlign="end">{formatCurrency(cart.point_discount)}

                            </Table.Cell>

                        </Table.Row>


                        {/* <Table.Row >
                            <Table.Cell>Giảm giá</Table.Cell>
                            <Table.Cell textAlign="end">{formatCurrency(cart.discount_amount)}
                                <div>
                                    {/* <Button variant={"ghost"}>
                                        Mã khuyến mãi
                                    </Button> 
                                    <VoucherInput></VoucherInput>
                                </div>
                            </Table.Cell>

                        </Table.Row> */}


                        <Table.Row >
                            <Table.Cell>Phí ship</Table.Cell>
                            <Table.Cell textAlign="end">{formatCurrency(cart.shipping_amount)}</Table.Cell>
                        </Table.Row>

                        <Table.Row >
                            <Table.Cell>Tổng tiền</Table.Cell>
                            <Table.Cell textAlign="end">
                                {formatCurrency(cart.total_amount)}</Table.Cell>
                        </Table.Row>

                    </Table.Body>
                </Table.Root>



                <div >
                    <Center>
                        <Button onClick={e => navigate("/")}>
                            Thêm sản phẩm
                        </Button>

                    </Center>
                </div>

            </VStack >

            <div style={{ position: "fixed", bottom: 0, left: 0, padding: 0, width: "100vw" }}>


                <Container maxW={"md"} style={{ padding: 0 }} >


                    <Box bg="bg" shadow="md">
                        <div style={{ display: "flex", padding: 16, }}>
                            <div style={{ flexGrow: 1 }}>
                                <Center height={"100%"}>
                                    <Text fontWeight="semibold" >
                                        {formatCurrency(cart?.total_amount)}
                                    </Text>

                                </Center>

                            </div>
                            <div style={{ display: "flex", gap: 8 }}>


                                {/* <div>
                                <Button>Thêm</Button>
                            </div> */}
                                <div>
                                    <Button onClick={goToCheckOut}>Đặt hàng</Button>
                                </div>
                            </div>
                        </div>
                    </Box>



                </Container>

            </div>


            <DrawerRoot open={lineToEdit ? true : false} onOpenChange={(e) => setLineToEdit(null)} placement="bottom" >
                <DrawerBackdrop />

                <DrawerContent  >
                    <Container maxW={"md"} maxHeight={"100vh"} style={{ overflow: "scroll", paddingBottom: 75 }}  >
                        <DrawerHeader>
                            <DrawerTitle>Sửa giỏ hàng</DrawerTitle>
                        </DrawerHeader>
                        <DrawerBody >
                            {lineToEdit &&
                                <Stack gap="4">
                                    <div style={{ display: "flex" }}>

                                        <div style={{ flex: 0.6 }}>
                                            <AspectRatio ratio={1}>
                                                <Image src={getLineItemImageSrc(lineToEdit)}>

                                                </Image>
                                                {/* <div>
                                                    {JSON.stringify(lineToEdit.product.images[0])}
                                                </div> */}
                                            </AspectRatio>
                                        </div>
                                        <div style={{ flex: 0.4, marginLeft: 16 }}>

                                        </div>
                                    </div>
                                    <Text fontWeight="semibold">{lineToEdit?.product?.name} {lineToEdit?.variant?.variant_title || ""}</Text>
                                    <div>
                                        <StepperInput
                                            value={lineToEdit.quantity}
                                            max={inventoryErrorsByVariant[lineToEdit.product_id] ? Math.max(0, Number(inventoryErrorsByVariant[lineToEdit.product_id].available ?? 0)) : undefined}
                                            onValueChange={e => { setLineToEdit({ ...lineToEdit, quantity: e.valueAsNumber }) }}
                                        ></StepperInput>
                                    </div>

                                    <Text fontWeight="semibold">Giá : {formatCurrency(itemCaculatedPrice)}</Text>
                                    <div>
                                        {lineToEdit &&
                                            <div>
                                                {(() => {
                                                    const priceRows = getPriceRowsForLineEdit(lineToEdit);
                                                    if (!priceRows.length) {
                                                        return null;
                                                    }
                                                    return (
                                                <Table.Root size="sm">
                                                    <Table.Header>
                                                        <Table.Row>
                                                            <Table.ColumnHeader>Số Lượng</Table.ColumnHeader>
                                                            <Table.ColumnHeader textAlign="end">Giá</Table.ColumnHeader>
                                                        </Table.Row>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {priceRows.map((item, index) => (
                                                            <Table.Row key={item?.id || `${item?.min || 1}-${item?.price || 0}-${index}`}>
                                                                <Table.Cell>Từ {item?.min || 1}</Table.Cell>

                                                                <Table.Cell textAlign="end">
                                                                    <Button variant={"ghost"} onClick={e => { setLineToEdit({ ...lineToEdit, quantity: Number(item?.min || 1) }) }}>
                                                                        {formatCurrency(Number(item?.price || 0))}
                                                                    </Button>

                                                                </Table.Cell>
                                                            </Table.Row>
                                                        ))}
                                                    </Table.Body>
                                                </Table.Root>
                                                    )
                                                })()}
                                            </div>
                                        }

                                    </div>
                                </Stack>
                            }

                        </DrawerBody>

                        <div style={{ position: "fixed", bottom: 0, left: 0, width: "100vw" }}>

                            <Container maxW={"md"} style={{ padding: 0 }} >
                                <Box bg="bg" shadow="md" borderRadius="md" style={{ padding: 8 }}>
                                    <Flex gap={4} style={{ width: "100%" }}>
                                        <Flex grow={1}>
                                            <Button onClick={onRemoveLineItem} variant={"ghost"}>Xoá</Button>
                                        </Flex>
                                        <Flex gap={4}>
                                            <DrawerActionTrigger asChild>
                                                <Button variant="outline">Huỷ</Button>
                                            </DrawerActionTrigger>
                                            <Button onClick={onUpdateLineItem} >Cập nhật</Button>
                                        </Flex>

                                    </Flex>

                                </Box>

                            </Container>




                        </div>
                        <DrawerCloseTrigger />
                    </Container>
                </DrawerContent>
            </DrawerRoot>

        </div >
    )
}

const EmptyCart = () => {
    const { setProcessing, setPageTitle, showToast, setShowProductSearch } = useApp();
    return (

        <Container maxWidth={"sm"}>
            <Stack>



                <Center>
                    <Image src="https://storage.googleapis.com/cubebio/default/empty-cart.svg" style={{ maxWidth: "50%" }}></Image>

                </Center>

                <Center>
                    Bạn chưa thêm sản phẩm nào vào giỏ hàng
                </Center>
                <Center>
                    <Button onClick={e => setShowProductSearch(true)}>
                        Thêm sản phẩm
                    </Button>

                </Center>
            </Stack>
        </Container>

    )
}

export default memo(Cart)